import { AlertTriangle, CheckCircle2, Ban } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui";
import { LizenzBadge } from "../components/Lizenz";
import { TRACE_META, traceWindowLabel } from "../../data/traceMeta";
import { systemLicensing } from "../../data/licensing";
import { MODEL_EXPLAIN, type LicenseModel } from "../../domain/licensing";
import { useApp } from "../../store/AppStore";
import { formatNum } from "../../domain/rules";

const RULES = [
  { id: "R1", title: "Personen, nicht Konten", body: "Lizenzbedarf zählt je Person. Ein Mensch mit mehreren Konten oder in mehreren Landschaften zählt gesamt genau einmal — vorausgesetzt, die Identitätsauflösung führt die Konten zusammen." },
  { id: "R2", title: "Klasse = Maximum, nie Summe", body: "Hat eine Person mehrere berechtigungstreibende Objekte, bestimmt das höchste die Lizenzklasse. Klassen werden nicht addiert." },
  { id: "R3", title: "Hebel wirken effektiv", body: "Eine Maßnahme reduziert eine Person nur, wenn kein zweiter Treiber sie weiterhin oben hält. Gezählt wird die tatsächlich erreichbare Reduktion, nicht die theoretische." },
  { id: "R4", title: "Warenkorb ist Zielzustand", body: "Mehrere Maßnahmen ergeben einen gemeinsamen Zielzustand. Personen im Schnitt zweier Maßnahmen zählen einmal — die Summe der Einzelreduktionen wird dadurch kleiner (I5)." },
];

const COVERED = [
  "Berechtigungsbasierter FUE-Bedarf (Full Use Equivalent) je Person",
  "Identitätsauflösung über Benutzername, Personalnummer oder E-Mail — inklusive Kollisionen",
  "Verhältnistabelle (Gewicht je Lizenzklasse) und Preis je FUE als freie Parameter",
  "Optimierungshebel mit Trace-Bewertung (nie genutzt / genutzt / kein Trace)",
  "Landschafts- und Cutover-Sicht mit Berechtigungsrisiko je Landschaft",
];

const NOT_COVERED = [
  { title: "BTP / CPEA (Consumption)", why: "Cloud-Verbrauch ist eine eigene, volumenbasierte Metrik — nicht in FUE umrechenbar." },
  { title: "Digital Access", why: "Indirekte Nutzung wird nach Belegen bemessen, nicht nach Personen." },
  { title: "Engine-Lizenzen (GTS, EWM, TM)", why: "Bemessen sich nach Volumen (Belege, Lieferpositionen, Transportaufträge). Das Werkzeug führt sie als eigene Metriken, rechnet sie aber nie in FUE." },
  { title: "Altes nutzungsbasiertes Lizenzmodell", why: "Vor-RISE-Metrik — bewusst nicht Gegenstand dieses Werkzeugs." },
];

export function Methodik() {
  const { ds, kpis } = useApp();

  return (
    <div>
      <PageHeader route="/methodik" />

      <div className="mb-6 rounded-lg border-soft p-4 text-sm" style={{ borderWidth: 1, backgroundColor: "var(--surface-sunken)" }}>
        <span className="text-normal font-medium">Ehrlichkeit über Stärken und Grenzen.</span>{" "}
        <span className="text-dim">
          Jede Zahl in diesem Werkzeug ist erklärbar und hängt an nachvollziehbaren Regeln. Diese Seite legt offen,
          welches Regelwerk zugrunde liegt, wie die Zahlen entstehen — und was bewusst nicht abgedeckt ist.
        </span>
      </div>

      <Card title="Das Regelwerk (R1–R4)" subtitle="Vier Regeln bestimmen jede FUE-Zahl. Sie stehen an den passenden Stellen auch in der Oberfläche." className="mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {RULES.map((r) => (
            <div key={r.id} className="rounded-lg p-4" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
              <div className="flex items-center gap-2">
                <span className="flex h-6 items-center rounded-md px-2 text-xs font-bold text-white" style={{ backgroundColor: "var(--color-brand-600)" }}>{r.id}</span>
                <span className="text-strong text-sm font-semibold">{r.title}</span>
              </div>
              <p className="text-dim mt-2 text-sm">{r.body}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card
        title="Welches System zählt wie?"
        subtitle="Nicht jedes System wird nach FUE lizenziert — und es ist kein Entweder-oder."
        className="mb-6"
      >
        <p className="text-dim text-sm">
          Ein System kann FUE für seine Dialognutzer tragen <span className="text-strong font-medium">und</span>{" "}
          zusätzlich eine eigene Volumenmetrik für sein Geschäftsvolumen. Deshalb unterscheidet das Werkzeug vier
          Kategorien statt eines Ja/Nein. In jede FUE-Summe gehen ausschließlich A und B ein.
        </p>

        <div className="mt-4 space-y-3">
          {(["fue", "fue+engine", "eigene-metrik", "keine"] as LicenseModel[]).map((m) => {
            const systeme = systemLicensing.filter((x) => x.model === m);
            return (
              <div key={m} className="rounded-lg p-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
                <div className="flex flex-wrap items-center gap-2">
                  <LizenzBadge model={m} />
                  <span className="text-faint font-mono text-xs">
                    {systeme.length > 0 ? systeme.map((x) => x.sid).join(" · ") : "—"}
                  </span>
                </div>
                <p className="text-dim mt-1.5 text-sm">{MODEL_EXPLAIN[m]}</p>
              </div>
            );
          })}
        </div>

        <p className="text-faint mt-4 text-xs">
          Belegt aus den Terminen: GSP ist das GTS-Produktivsystem. Recherchiert: EWM und TM sind in der
          Basic-Ausprägung im S/4HANA-Kern enthalten, nur Advanced ist zusätzlich lizenzpflichtig; HCM läuft unter
          Private Cloud auf einer Employee-Metrik. Wo eine Einstufung eine Annahme ist, steht das an der
          Landschaft selbst.
        </p>
      </Card>

      <Card title="Woher der Trace kommt" subtitle={"„Nie genutzt\" ist nur mit Zeitraum und Quelle eine Aussage."} className="mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg p-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
            <div className="text-faint text-xs font-medium uppercase tracking-wide">Zeitraum</div>
            <div className="text-strong mt-1 text-sm font-semibold">{traceWindowLabel()}</div>
          </div>
          <div className="rounded-lg p-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
            <div className="text-faint text-xs font-medium uppercase tracking-wide">Quelle</div>
            <div className="text-strong mt-1 text-sm font-semibold">{TRACE_META.source}</div>
          </div>
          <div className="rounded-lg p-3" style={{ borderWidth: 1, borderColor: "var(--border-soft)" }}>
            <div className="text-faint text-xs font-medium uppercase tracking-wide">Methode</div>
            <div className="text-dim mt-1 text-sm">{TRACE_META.method}</div>
          </div>
        </div>
        <p className="text-dim mt-3 text-sm">
          <span className="text-strong font-medium">„Kein Trace"</span> heißt: {TRACE_META.noTrace} Das wird nicht als „nie genutzt" verkauft — solche
          Konten sind nicht bewertbar und tragen nichts zur gefahrlosen Reduktion bei.{" "}
          <span className="text-strong font-medium">Gesperrte Konten</span> zählen in der PCE-Vermessung weiter, bis sie ungültig gesetzt oder gelöscht sind —
          Sperren allein senkt den Bedarf nicht.
        </p>
      </Card>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Was das Werkzeug abdeckt" subtitle="Der belegbare Kern.">
          <ul className="space-y-2">
            {COVERED.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "var(--color-amp-green)" }} />
                <span className="text-normal">{c}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Was bewusst nicht abgedeckt ist" subtitle="Benannt und ehrlich — keine erfundenen Zahlen." right={<AlertTriangle size={18} style={{ color: "var(--color-amp-amber)" }} />}>
          <ul className="space-y-3">
            {NOT_COVERED.map((n) => (
              <li key={n.title} className="flex items-start gap-2 text-sm">
                <Ban size={15} className="mt-0.5 shrink-0" style={{ color: "var(--color-amp-amber)" }} />
                <span>
                  <span className="text-strong font-medium">{n.title}</span>
                  <span className="text-faint"> — {n.why}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card title="Wie die Zahlen entstehen" subtitle="Von den Rohkonten bis zum ausgewiesenen Bedarf.">
        <ol className="space-y-3">
          {[
            { n: 1, t: "Erhebung", d: `${formatNum(ds.accounts.length)} Konten aus dem Benutzerstamm, Rollen und Berechtigungswerten — je Konto System- und Mandantenkennung.` },
            { n: 2, t: "Identitätsauflösung", d: `Konten werden über den gewählten Schlüssel zu Personen zusammengeführt. Fehlt der Schlüssel oder ist er mehrdeutig, wird konservativ nicht zusammengeführt.` },
            { n: 3, t: "Klassenermittlung (R2)", d: "Je Person bestimmt das höchste treibende Objekt die Lizenzklasse." },
            { n: 4, t: "Gewichtung", d: "Jede Klasse trägt ihr Gewicht aus der Verhältnistabelle bei — die Summe ergibt den FUE-Bedarf." },
            { n: 5, t: "Bewertung & Optimierung (R3/R4)", d: "Hebel zeigen die effektiv erreichbare Reduktion; der Warenkorb rechnet als Zielzustand." },
          ].map((s) => (
            <li key={s.n} className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: "var(--color-brand-600)" }}>{s.n}</span>
              <div>
                <div className="text-strong text-sm font-semibold">{s.t}</div>
                <div className="text-dim text-sm">{s.d}</div>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-4 rounded-lg p-3 text-sm" style={{ backgroundColor: "var(--surface-sunken)" }}>
          <span className="text-dim">Aktueller ausgewiesener Bedarf: </span>
          <span className="tnum text-strong font-semibold">{formatNum(kpis.ist)} FUE</span>
          <span className="text-faint"> gegen {formatNum(kpis.entitlement)} FUE vertraglich — die Herleitung steht Seite für Seite offen.</span>
        </div>
      </Card>

      <p className="text-faint mt-6 text-xs">
        Die Zahlen dieses Prototyps beruhen auf deterministisch erzeugten Beispieldaten (Seed {ds.meta.generatedFrom}),
        die die Größenordnung von Arvato SE nachbilden — kein Produktivsystem.
      </p>
    </div>
  );
}
